import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { registrationApi } from "@/lib/api/registration";
import { CreditCard, AlertCircle, FileText } from "lucide-react";

const CURRENCY_SYMBOL = "₦";

export function PaymentPrompt() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<{
    paymentRequired: boolean;
    paymentCompleted: boolean;
    registrationFee: number;
    schoolShare?: number;
    canAccessServices: boolean;
  } | null>(null);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    checkPaymentStatus();
  }, []);

  const checkPaymentStatus = async () => {
    try {
      setLoading(true);
      const status = await registrationApi.checkPaymentStatus();
      setPaymentStatus(status);
    } catch (error: any) {
      console.error("Failed to check payment status:", error);
      setError(error.response?.data?.message || "Failed to check payment status");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  if (!paymentStatus) {
    return null;
  }

  if (!paymentStatus.paymentRequired || paymentStatus.paymentCompleted) {
    return null;
  }

  return (
    <div className="mx-auto mb-6 w-full max-w-7xl">
      <Card className="border-slate-200 bg-white shadow-sm">
        <CardHeader className="border-b border-slate-100 pb-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <CreditCard className="h-5 w-5 text-primary" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <CardTitle className="font-heading text-lg text-slate-900">
                Payment required
              </CardTitle>
              <CardDescription className="mt-1 text-slate-600">
                Complete your registration by paying the registration fee.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          {error && (
            <Alert variant="destructive" className="items-start">
              <div className="flex gap-3">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <AlertDescription>{error}</AlertDescription>
              </div>
            </Alert>
          )}

          <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium text-slate-700">Registration fee</span>
              <span className="text-2xl font-bold tabular-nums text-slate-900">
                {CURRENCY_SYMBOL}
                {(paymentStatus.schoolShare != null && paymentStatus.schoolShare > 0
                  ? paymentStatus.schoolShare
                  : paymentStatus.registrationFee
                ).toLocaleString()}
              </span>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              You have an outstanding invoice. Open your invoices to pay online and get full access
              to library services.
            </p>
          </div>

          <Button onClick={() => navigate("/invoices")} className="min-h-11 w-full gap-2" size="lg">
            <FileText className="h-4 w-4" />
            {"View invoices & pay"}
          </Button>

          <p className="text-center text-xs text-slate-500">
            Library services stay limited until payment is completed.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
